import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { getMergedTheme, CustomPage, PageBlock, PageSection, PageColumn } from '@/lib/themeConfig';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SearchModal from '@/components/feature/SearchModal';
import LoginModal from '@/components/feature/LoginModal';

// ─── Block Renderers ──────────────────────────────────────────────────────────

function RenderBlock({ block }: { block: PageBlock }) {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  switch (block.type) {
    case 'heading': {
      const Tag = block.level as keyof JSX.IntrinsicElements;
      const sizeMap = { h1: 'text-4xl font-bold', h2: 'text-3xl font-bold', h3: 'text-2xl font-semibold', h4: 'text-xl font-semibold' };
      const alignMap = { left: 'text-left', center: 'text-center', right: 'text-right' };
      return <Tag className={`${sizeMap[block.level]} ${alignMap[block.align]} leading-tight`} style={{ color: block.color }}>{block.text}</Tag>;
    }
    case 'text': {
      const alignMap = { left: 'text-left', center: 'text-center', right: 'text-right' };
      return <p className={`text-base leading-relaxed whitespace-pre-line ${alignMap[block.align]}`} style={{ color: block.color }}>{block.content}</p>;
    }
    case 'image':
      return (
        <div>
          <div className="w-full overflow-hidden" style={{ borderRadius: `${block.border_radius}px` }}>
            <img src={block.image_url} alt={block.alt} className="w-full h-auto object-cover object-top" />
          </div>
          {block.caption && <p className="text-center text-sm text-stone-400 mt-2">{block.caption}</p>}
        </div>
      );
    case 'button': {
      const alignMap = { left: 'text-left', center: 'text-center', right: 'text-right' };
      const sizeMap = { sm: 'px-4 py-2 text-sm', md: 'px-6 py-2.5 text-sm', lg: 'px-8 py-3 text-base' };
      const variantMap = {
        primary: 'bg-amber-600 text-white hover:bg-amber-700',
        secondary: 'bg-stone-800 text-white hover:bg-stone-900',
        outline: 'border-2 border-stone-800 text-stone-800 hover:bg-stone-800 hover:text-white',
      };
      return (
        <div className={alignMap[block.align]}>
          <Link to={block.href} className={`inline-block rounded-full font-medium transition-colors whitespace-nowrap ${sizeMap[block.size]} ${variantMap[block.variant]}`}>{block.label}</Link>
        </div>
      );
    }
    case 'divider':
      return <hr style={{ borderColor: block.color }} />;
    case 'spacer':
      return <div style={{ height: `${block.height}px` }} />;
    case 'hero':
      return (
        <div className="relative w-full h-[400px] md:h-[500px] flex items-center justify-center overflow-hidden rounded-xl">
          {block.image_url && <img src={block.image_url} alt={block.title} className="absolute inset-0 w-full h-full object-cover object-top" />}
          <div className="absolute inset-0 rounded-xl" style={{ backgroundColor: `rgba(0,0,0,${block.overlay_opacity / 100})` }} />
          <div className="relative z-10 text-center px-6 max-w-2xl">
            {block.subtitle && <p className="text-xs font-medium text-white/70 uppercase tracking-widest mb-2">{block.subtitle}</p>}
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">{block.title}</h2>
            {block.description && <p className="text-white/80 mb-6 text-sm leading-relaxed">{block.description}</p>}
            {block.cta_text && <Link to={block.cta_link || '/'} className="inline-block bg-amber-600 text-white px-6 py-2.5 rounded-full font-medium hover:bg-amber-700 transition-colors whitespace-nowrap">{block.cta_text}</Link>}
          </div>
        </div>
      );
    case 'faq_list':
      return (
        <div>
          {block.title && <h3 className="text-xl font-bold text-stone-800 mb-4">{block.title}</h3>}
          <div className="space-y-2">
            {block.items.map(item => (
              <div key={item.id} className="border border-stone-200 rounded-xl overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === item.id ? null : item.id)} className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer hover:bg-stone-50 transition-colors">
                  <span className="text-sm font-medium text-stone-800">{item.question}</span>
                  <i className={`${openFaq === item.id ? 'ri-subtract-line' : 'ri-add-line'} text-stone-400 flex-shrink-0 ml-3`} />
                </button>
                {openFaq === item.id && <div className="px-4 pb-3 text-sm text-stone-600 leading-relaxed border-t border-stone-100 pt-3">{item.answer}</div>}
              </div>
            ))}
          </div>
        </div>
      );
    case 'features_grid':
      return (
        <div>
          {block.title && <h3 className="text-xl font-bold text-stone-800 mb-4">{block.title}</h3>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {block.items.map(item => (
              <div key={item.id} className="bg-stone-50 rounded-xl p-4">
                <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-lg mb-3">
                  <i className={`${item.icon} text-amber-600`} />
                </div>
                <h4 className="text-sm font-semibold text-stone-800 mb-1">{item.title}</h4>
                <p className="text-xs text-stone-500 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      );
    case 'stats':
      return (
        <div>
          {block.title && <h3 className="text-xl font-bold text-stone-800 mb-4 text-center">{block.title}</h3>}
          <div className="grid grid-cols-2 gap-4">
            {block.items.map(item => (
              <div key={item.id} className="text-center bg-amber-50 rounded-xl p-4">
                <p className="text-2xl font-bold text-amber-700">{item.value}</p>
                <p className="text-xs text-stone-500 mt-1">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      );
    case 'team':
      return (
        <div>
          {block.title && <h3 className="text-xl font-bold text-stone-800 mb-4">{block.title}</h3>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {block.members.map(m => (
              <div key={m.id} className="bg-white border border-stone-200 rounded-xl overflow-hidden">
                <div className="w-full h-40 overflow-hidden">
                  <img src={m.image_url} alt={m.name} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-4">
                  <p className="text-sm font-semibold text-stone-800">{m.name}</p>
                  <p className="text-xs text-amber-600 font-medium mt-0.5">{m.role}</p>
                  <p className="text-xs text-stone-500 mt-2 leading-relaxed">{m.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    default:
      return null;
  }
}

// ─── Column Width to CSS ──────────────────────────────────────────────────────
function colStyle(w: string): React.CSSProperties {
  return { width: `${w}%`, flexShrink: 0 };
}

// ─── Section Renderer ─────────────────────────────────────────────────────────
function RenderSection({ section }: { section: PageSection }) {
  const paddingMap: Record<string, string> = {
    none: '0px', sm: '16px', md: '40px', lg: '64px', xl: '96px',
  };
  return (
    <section style={{ backgroundColor: section.background_color, paddingTop: paddingMap[section.padding_y], paddingBottom: paddingMap[section.padding_y] }}>
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          {section.columns.map((col: PageColumn) => (
            <div key={col.id} className="w-full space-y-4" style={colStyle(col.width)}>
              {col.blocks.map((block: PageBlock) => (
                <RenderBlock key={block.id} block={block} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function DynamicPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchOpen, setSearchOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  // Read from localStorage every render so changes are always fresh
  const theme = getMergedTheme();
  const page: CustomPage | undefined = theme.pages?.find(p => p.seo.slug === slug);

  if (!page) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar onSearchOpen={() => setSearchOpen(true)} onLoginOpen={() => setLoginOpen(true)} />
        <main className="flex-1 flex items-center justify-center pt-20">
          <div className="text-center">
            <p className="text-6xl font-bold text-stone-200 mb-4">404</p>
            <p className="text-lg font-semibold text-stone-700 mb-2">Sayfa Bulunamadı</p>
            <p className="text-sm text-stone-400 mb-6">Aradığınız sayfa mevcut değil.</p>
            <Link to="/" className="inline-block bg-amber-600 text-white px-6 py-2.5 rounded-full font-medium hover:bg-amber-700 transition-colors whitespace-nowrap">Ana Sayfaya Dön</Link>
          </div>
        </main>
        <Footer />
        <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
        <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar onSearchOpen={() => setSearchOpen(true)} onLoginOpen={() => setLoginOpen(true)} />
      <main className="flex-1 pt-20">
        {page.sections && page.sections.length > 0
          ? page.sections.map(section => <RenderSection key={section.id} section={section} />)
          : (
            <div className="py-32 text-center text-stone-400">
              <i className="ri-pages-line text-4xl mb-3 block" />
              <p className="text-sm">Bu sayfa henüz içerik içermiyor.</p>
            </div>
          )
        }
      </main>
      <Footer />
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      <LoginModal isOpen={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  );
}
